import React, { useState } from "react";
import { Dialog, DialogFooter } from "../../components/ui/Dialog";
import { Button } from "../../components/ui/Button";
import { createCategory } from "../../services/caregory";

interface AddCategoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdded?: () => void;
}

const AddCategoryDialog: React.FC<AddCategoryDialogProps> = ({
  isOpen,
  onClose,
  onAdded,
}) => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [newSubCategory, setNewSubCategory] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAddSubCategory = () => {
    const trimmed = newSubCategory.trim();
    if (!trimmed) return;
    if (subCategories.includes(trimmed)) {
      alert("This subcategory is already added.");
      return;
    }
    setSubCategories((prev) => [...prev, trimmed]);
    setNewSubCategory("");
  };

  const handleRemoveSubCategory = (sub: string) => {
    setSubCategories((prev) => prev.filter((s) => s !== sub));
  };

  const handleAdd = async () => {
    if (!code.trim() || !name.trim()) {
      alert("Please enter both code and name.");
      return;
    }

    setLoading(true);
    try {
      await createCategory({
        code: code.toUpperCase().trim(),
        name: name.trim(),
        subCategory: subCategories, // 👈 include subCategory array
      });
      onAdded?.();

      // Reset form fields
      setCode("");
      setName("");
      setSubCategories([]);
      setNewSubCategory("");

      onClose();
    } catch (err) {
      console.error(err);
      alert((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add Category"
      size="md"
      footer={
        <DialogFooter>
          <Button onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={loading}>
            {loading ? "Adding..." : "Add"}
          </Button>
        </DialogFooter>
      }
    >
      <div className="space-y-4">
        {/* Code */}
        <div>
          <label className="block text-sm font-medium">Code</label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full px-2 py-1 border rounded"
          />
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-2 py-1 border rounded"
          />
        </div>

        {/* SubCategory Section */}
        <div>
          <label className="block text-sm font-medium">Subcategories</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newSubCategory}
              onChange={(e) => setNewSubCategory(e.target.value)}
              placeholder="Enter subcategory"
              className="flex-1 px-2 py-1 border rounded"
              onKeyDown={(e) => e.key === "Enter" && handleAddSubCategory()}
            />
            <Button
              variant="secondary"
              type="button"
              onClick={handleAddSubCategory}
            >
              Add
            </Button>
          </div>

          {/* Show added subcategories */}
          <div className="flex flex-wrap gap-2 mt-2">
            {subCategories.map((sub) => (
              <div
                key={sub}
                className="flex items-center gap-1 bg-gray-100 text-sm px-2 py-1 rounded-full"
              >
                <span>{sub}</span>
                <button
                  type="button"
                  className="text-red-500 hover:text-red-700 font-semibold"
                  onClick={() => handleRemoveSubCategory(sub)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default AddCategoryDialog;
